import type { KeyboardEvent } from 'react';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { cn } from '@/libs/utils/cn';

interface DealFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'email';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Marks the field the step will not move on without: a star, and `aria-required` for readers. */
  required?: boolean;
  /**
   * The field's own action, shown as a button inside it and also bound to Enter. For a value
   * checked against the CRM once, on an explicit act — never as the BD types, and never on a
   * stray blur.
   */
  action?: { label: string; onAct: () => void };
  onFocus?: () => void;
  onBlur?: () => void;
  /** Keys the field's owner handles first, such as moving through a list under it. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}

/** One text field of the creation form. Every step's inputs are this shape. */
export function DealField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  action,
  onFocus,
  onBlur,
  onKeyDown,
}: DealFieldProps) {
  const act = () => {
    if (value.trim() === '') return;
    action?.onAct();
  };

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {/* The star is generated content, so it stays out of the label's accessible name — readers
          get `aria-required` on the input instead of a stray asterisk. */}
      <Label
        htmlFor={id}
        className={cn(
          'text-xs font-normal text-primary-500',
          required && 'gap-0.5 after:text-destructive after:content-["*"]',
        )}
      >
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        autoComplete="off"
        // Keeps the password managers' autofill off a CRM form they mistake for a signup.
        data-form-type="other"
        data-1p-ignore
        data-lpignore="true"
        // Wide enough right padding that a typed value never runs under the action's label.
        className={cn('h-[42px] min-h-0', action && 'pr-20')}
        value={value}
        placeholder={placeholder}
        aria-required={required || undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.defaultPrevented || !action) return;
          if (event.code !== 'Enter' && event.code !== 'NumpadEnter') return;
          event.preventDefault();
          act();
        }}
        endIcon={
          action && (
            <button
              type="button"
              onClick={act}
              disabled={value.trim() === ''}
              className="rounded-md px-2 py-1 text-xs font-medium text-brand-800 transition-opacity hover:opacity-70 disabled:pointer-events-none disabled:text-primary-400"
            >
              {action.label}
            </button>
          )
        }
      />
    </div>
  );
}
